import { TFunction } from "i18next";

import { SelectOption } from "features/ui/Select";

/**
 * Stable chart y-axis option ids (the data keys). These are language-independent
 * and safe to import wherever only the id is needed (e.g. comparing a selected
 * option). Display labels come from the `get*Option` factories below.
 */
export const CHART_ACTION_ID = {
  cumulativeCPV: "cumulativeCPV",
  // Preserves the historical (mismatched) id — do not "fix" without migrating
  // any persisted chart settings that reference it.
  cumulativeDPTV: "cumulativeIPTV",
  cumulativeIPTV: "cumulativeIPTV",
  cumulativeDPTVDV: "cumulativeDPTV",
  totalVehicles: "numVehicles",
  cumulativeEvents: "numEvents",
  FRCPercent: "FRCPercent",
} as const;

export const getMeasureLabel = (t: TFunction): string => t("analytics.measure");

export const getCumulativeCPVOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.cumulativeCPV,
  value: t("analytics.cumulative_cpv_value"),
  label: t("analytics.cumulative_cpv_label"),
});

export const getCumulativeDPTVOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.cumulativeDPTV,
  value: t("analytics.cumulative_dptv"),
  label: t("analytics.cumulative_dptv"),
});

export const getCumulativeIPTVOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.cumulativeIPTV,
  value: t("analytics.by_age.estimated_events_label"),
  label: t("analytics.by_age.estimated_events_label"),
});

export const getCumulativeDPTVDVOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.cumulativeDPTVDV,
  value: t("analytics.cumulative_dptv_dv_value"),
  label: t("analytics.cumulative_dptv_dv_label"),
});

export const getTotalVehiclesOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.totalVehicles,
  value: t("event_type.vehicle_other"),
  label: t("event_type.vehicle_other"),
});

export const getCumulativeEventsOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.cumulativeEvents,
  value: t("event_type.event_other"),
  label: t("event_type.event_other"),
});

export const getFRCPercentOption = (t: TFunction): SelectOption => ({
  id: CHART_ACTION_ID.FRCPercent,
  value: t("analytics.by_age.frc_label"),
  label: t("analytics.by_age.frc_label"),
});

/**
 * Ids meaning "total events" across the different chart y-axis option sets.
 * Total events is the default measure for every chart; the first of these
 * present in a chart's options wins.
 */
const TOTAL_EVENTS_OPTION_IDS: string[] = [
  CHART_ACTION_ID.cumulativeEvents,
  "numSignalEventOccurrences",
  "events",
];

export const getTotalEventsOptionId = (
  options: SelectOption[] | undefined
): string | undefined =>
  TOTAL_EVENTS_OPTION_IDS.find((id) =>
    options?.some((option) => option.id === id)
  );
